/// <reference lib="webworker" />
import { unescape } from 'lodash';
import * as FlatModels from '@medsurf/flat-models';
import { parse } from 'node-html-parser';

export const textExportFuncs = {
  TextStructured: (argObj: FlatModels.TextExportAuthorModels.TextExportArgType) => {
    const preparedItems = cleanTextDeep(argObj.textExports, argObj.keepHTMLTags);
    return outputStructuredText(preparedItems);
  },
  TextWithoutInfo: (argObj: FlatModels.TextExportAuthorModels.TextExportArgType) => {
    const preparedItems = cleanTextDeep(argObj.textExports, argObj.keepHTMLTags);
    return outputPlainText(preparedItems);
  },
  TextInJson: (argObj: FlatModels.TextExportAuthorModels.TextExportArgType) => {
    const preparedItems = cleanTextDeep(argObj.textExports, argObj.keepHTMLTags);
    return JSON.stringify(preparedItems, null, 4);
  }
}

const replaceKeySet = new Set(['header', 'description', 'body', 'text', 'content', 'solutionText', 'explanation', 'label', 'name', 'comments', 'note']);
const infoKeysMap = new Set(['id', 'slideId', 'type', 'editedAt', 'nodeId', 'hidden', 'route', 'pdfPath', 'authorPdfPath', 'media', 'solution']);
const titleKeySet = new Set(['title', 'header', 'body', 'pois', 'annotations', 'questions', 'explanation']);


function cleanTextDeep(obj: any, keepHTML = false, key?: string): any {
  if (typeof obj === 'object') {
    // Array
    if (Array.isArray(obj)) {
      return obj.map((item: any) => cleanTextDeep(item, keepHTML));
    }

    // Record
    const newObj = structuredClone(obj);
    for (const key in newObj) {
      newObj[key] = cleanTextDeep(newObj[key], keepHTML, key);
    }
    return newObj;
  }

  if (typeof obj === 'string') {
    return key && replaceKeySet.has(key) ? cleanText(obj, keepHTML) : obj;
  }

  return obj;
}

function outputPlainText(items: any[], firstLevel = true) {
  let output = '';

  items.forEach((item) => {
    if (typeof item === 'object') {

      if (firstLevel) {
        output += '<URL>\n';
        output += `${location.origin}/${item.route}\n`;
        output += '\n';
      }

      for (const key in item) {
        if (infoKeysMap.has(key)) {
          continue;
        }

        const value = item[key];
        if (!value) continue;

        if (Array.isArray(value)) {
          if (value.length > 0) {
            if (titleKeySet.has(key)) {
              output += `<${key.toUpperCase()}>\n`;
            }
            output += outputPlainText(value, false);
            if (titleKeySet.has(key)) output += '\n';
          }
        } else {
          if (titleKeySet.has(key)) {
            output += `<${key.toUpperCase()}>\n`;
          }
          output += `${value}\n`
          if (titleKeySet.has(key)) output += '\n';

        }
      }

      if (firstLevel) {
        output += '--------------------------------------------------------------------------------\n\n';
      } else {
        output += '---\n'
      }
    }
  })
  return output;
}


function outputStructuredText(items: any[], indent = 0) {
  let output = '';
  items.forEach((item) => {
    let first = true;
    let key: keyof typeof item;
    for (key in item) {
      if (infoKeysMap.has(key)) {
        continue;
      }

      const value = item[key];
      const printKey = key.charAt(0).toUpperCase() + key.slice(1);


      if (Array.isArray(value)) {
        if (value.length > 0) {
          output += `${''.padStart(indent, ' ')}${first ? '- ' : '  '}${printKey}:\n`;
          output += outputStructuredText(value, indent + 4);
        }
        continue;
      }

      if (value) {
        output += `${''.padStart(indent, ' ')}${first ? '- ' : '  '}`;
        output += `${printKey}: `;
        const preparedValue = typeof value === 'string' ? value.replaceAll('\n', ' ') : value;
        output += `${preparedValue}\n`;
      }
      first = false;
    }
  });
  return output;
}

function cleanText(text: string, keepHTMLTags: boolean) {
  if (!keepHTMLTags) {
    const parsed = parse(text || '');
    text = parsed.structuredText;
  }

  return unescape(text || '')
    ?.replace(/&auml;/g, 'ä')
    .replace(/&ouml;/g, 'ö')
    .replace(/&uuml;/g, 'ü')
    .replace(/&Auml;/g, 'Ä')
    .replace(/&Ouml;/g, 'Ö')
    .replace(/&Uuml;/g, 'Ü')
    .replace(/&nbsp;/g, ' ')
    .replace(/  +/g, ' ')
    .trim();
}

addEventListener('message', ({ data }: MessageEvent<{ argObj: FlatModels.TextExportAuthorModels.TextExportArgType }>) => {
  if (!data?.argObj) return;
  
  try {
    const func = textExportFuncs?.[data.argObj.type];
    if (func) {
      postMessage(func(data.argObj));
    } else {
      throw 'func not found';
    }
  } catch(e){
    postMessage(e);
  }
});
